import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, CheckCircle2, ShieldCheck, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';
import { useResolveFinding } from '../hooks/useResolveFinding';
import {
  CATEGORIES,
  TARGET_TYPES,
  resolveFindingFormSchema,
  rupeesStringToPaise,
  type ResolveFindingFormValues,
} from '../schemas';
import type { ReconciliationFindingRow, ResolveFindingPayload } from '../types';

interface ResolveFindingModalProps {
  finding: ReconciliationFindingRow | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ResolveFindingModal({
  finding,
  isOpen,
  onClose,
  onSuccess,
}: ResolveFindingModalProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const resolveMutation = useResolveFinding();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ResolveFindingFormValues>({
    resolver: zodResolver(resolveFindingFormSchema),
    defaultValues: {
      resolution: '',
      createAdjustment: false,
      targetType: 'PAYMENT',
      targetId: finding?.entityId ?? '',
      category: 'RECONCILIATION_CORRECTION',
      amountRupees: '0',
    },
    values: finding
      ? {
          resolution: '',
          createAdjustment: false,
          targetType: finding.entityType === 'Invoice' ? 'PURCHASE' : 'PAYMENT',
          targetId: finding.entityId,
          category: 'RECONCILIATION_CORRECTION',
          amountRupees: '0',
        }
      : undefined,
  });

  const createAdjustment = watch('createAdjustment');

  if (!isOpen || !finding) return null;

  const onSubmit = async (values: ResolveFindingFormValues) => {
    setServerError(null);

    const payload: ResolveFindingPayload = {
      resolution: values.resolution,
      ...(values.createAdjustment
        ? {
            adjustment: {
              targetType: values.targetType,
              targetId: values.targetId,
              amountPaise: rupeesStringToPaise(values.amountRupees),
              category: values.category,
            },
          }
        : {}),
    };

    try {
      await resolveMutation.mutateAsync({
        findingId: finding.id,
        payload,
      });
      reset();
      onClose();
      onSuccess?.();
    } catch (err: unknown) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to resolve reconciliation finding';
      setServerError(errorMsg);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          data-testid="resolve-finding-modal"
          className="w-full max-w-xl bg-surface-container-lowest border border-border-subtle rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="p-6 border-b border-border-subtle flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold uppercase px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  {finding.kind}
                </span>
                <span className="text-xs text-on-surface-variant font-mono truncate max-w-xs">
                  {finding.entityType} ({finding.entityId})
                </span>
              </div>
              <h2 className="text-lg font-bold text-on-surface">Resolve Reconciliation Finding</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-on-surface-variant hover:text-on-surface hover:bg-white/5 rounded-xl transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 overflow-y-auto space-y-5 flex-1">
            {serverError && (
              <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-start gap-3 text-rose-300 text-xs">
                <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                <p className="flex-1 font-medium">{serverError}</p>
              </div>
            )}

            {/* Resolution Note (Mandatory) */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex justify-between">
                <span>Resolution Explanation (Required)</span>
                <span className="text-[10px] text-on-surface-variant font-normal">Min 3 chars</span>
              </label>
              <textarea
                {...register('resolution')}
                data-testid="resolution-textarea"
                rows={3}
                placeholder="Describe what was investigated, verified in gateway, and done to remediate this finding..."
                className="w-full p-3 bg-surface-container/60 border border-border-subtle rounded-xl text-sm text-on-surface placeholder:text-on-surface-variant/60 focus:outline-none focus:border-primary transition-all resize-none"
              />
              {errors.resolution && (
                <p className="text-xs text-rose-400 font-medium">{errors.resolution.message}</p>
              )}
            </div>

            {/* Toggle Financial Adjustment */}
            <div className="pt-2 border-t border-border-subtle/60">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  {...register('createAdjustment')}
                  data-testid="create-adjustment-checkbox"
                  className="w-4 h-4 rounded border-border-subtle text-primary focus:ring-primary bg-surface-container/60"
                />
                <div>
                  <p className="text-sm font-semibold text-on-surface">
                    Create Financial Adjustment (Level 13 Ledger Correction)
                  </p>
                  <p className="text-xs text-on-surface-variant">
                    Appends an immutable correction row to balance the revenue ledger.
                  </p>
                </div>
              </label>
            </div>

            {/* Optional Financial Adjustment Fields */}
            {createAdjustment && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="p-4 rounded-2xl bg-surface-container/30 border border-border-subtle space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-on-surface-variant block mb-1">
                      Target Entity Type
                    </label>
                    <select
                      {...register('targetType')}
                      className="w-full px-3 py-2 bg-surface-container border border-border-subtle rounded-xl text-xs text-on-surface focus:outline-none focus:border-primary"
                    >
                      {TARGET_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-on-surface-variant block mb-1">
                      Adjustment Category
                    </label>
                    <select
                      {...register('category')}
                      className="w-full px-3 py-2 bg-surface-container border border-border-subtle rounded-xl text-xs text-on-surface focus:outline-none focus:border-primary"
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-on-surface-variant block mb-1">
                      Target ID
                    </label>
                    <input
                      type="text"
                      {...register('targetId')}
                      className="w-full px-3 py-2 bg-surface-container border border-border-subtle rounded-xl text-xs font-mono text-on-surface focus:outline-none focus:border-primary"
                    />
                    {errors.targetId && (
                      <p className="text-xs text-rose-400 mt-1">{errors.targetId.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-medium text-on-surface-variant block mb-1">
                      Amount in Rupees (₹)
                    </label>
                    <input
                      type="text"
                      {...register('amountRupees')}
                      placeholder="-200.00"
                      className="w-full px-3 py-2 bg-surface-container border border-border-subtle rounded-xl text-xs font-mono text-on-surface focus:outline-none focus:border-primary"
                    />
                    <p className="text-[10px] text-on-surface-variant mt-1">
                      Use negative values for write-offs/debits, positive for credits.
                    </p>
                    {errors.amountRupees && (
                      <p className="text-xs text-rose-400 mt-1">{errors.amountRupees.message}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Invariant Banner */}
            <div className="p-3 rounded-xl bg-surface-container/20 border border-border-subtle/50 flex items-start gap-2.5 text-xs text-on-surface-variant">
              <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <span>
                Resolving records your Admin ID and audit trail. Original purchase, payment, and refund records remain strictly immutable.
              </span>
            </div>

            {/* Footer Buttons */}
            <div className="pt-3 border-t border-border-subtle flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 rounded-xl border border-border-subtle text-xs text-on-surface-variant hover:text-on-surface hover:bg-white/5 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="confirm-resolve-btn"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-primary hover:bg-primary-hover text-on-primary font-bold text-xs shadow-md transition-all disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span>Resolving...</span>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Confirm Resolution</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
